import React, { useState, useEffect } from 'react'

import { useRouter } from 'next/router'
import PageContent from '@components/layout/PageContent'
import useAuthenticationApi from '@hooks/useAuthenticationApi'
import { getXCLEARCorrelationInfo } from '@api/utils'
import { Form, StepSubTitle, StepTitle } from '@components/layout/Form'
import { Enrollment as EnrollmentClient } from '@clear-denver/tpc-openapi-typescript'
import {
  Box,
  Checkbox,
  Flex,
  InputGroup,
  Link,
  Text,
  Button,
} from '@clear/design-system'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import VerificationCodeForm from '@components/VerificationCodeForm'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { authSchema } from '@validators/schema/enroll/authSchema'
import { useEnrollmentContext } from '@contexts/EnrollmentContext'
import { trackFunnelStep } from '@utils/trackFunnelStep'

interface FormData extends Partial<EnrollmentClient.BiographicsLookup> {
  otpSessionId: string
}

type InitiateAuth = {
  phoneNumber?: string
  email?: string
  otpSessionId: string
}

const AuthPage: React.FC = () => {
  const router = useRouter()
  const { client: authenticationClient } = useAuthenticationApi()
  const { setBiographicAuthInfo, setApptInfo } = useEnrollmentContext()

  const [verificationFormData, setVerificationFormData] =
    useState<FormData | null>(null)
  const { register, handleSubmit, formState } = useForm<any>({
    resolver: yupResolver(authSchema),
    mode: 'onChange',
  })

  enum FormSteps {
    CONTACT = 'Contact',
    VERIFICATION = 'Verification',
  }

  useEffect(() => {
    setApptInfo(null)
  }, [setApptInfo])

  const [step, setStep] = useState<FormSteps>(FormSteps.CONTACT)
  const { errors, isValid } = formState
  const [otpFailureMessage, setOtpFailureMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const desiredApply = router.query?.desiredPath === '/enroll/you'
  const desiredRenew =
    router.query?.desiredPath === '/renewal/eligibility/eligibility'
  useEffect(() => {
    if (desiredApply) {
      trackFunnelStep('apply', 2, 'auth')
    }
    if (desiredRenew) {
      trackFunnelStep('renew', 2, 'auth')
    }
  }, [desiredApply, desiredRenew])

  async function onSubmitContact(formData: InitiateAuth) {
    setIsLoading(true)
    const { phoneNumber, email } = formData
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
      url: router.route,
    }
    const formatedPhoneNumber = phoneNumber ? `+1.${phoneNumber}` : null
    dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
      payload: {
        message: 'Initiate auth begin',
      },
      metadata: {
        ...baseMetaData,
        event: DiscreteEvent.INITIATE_AUTH_BEGIN,
      },
    })
    try {
      const response = await authenticationClient.initiateAuth({
        xCLEARCorrelationInfo,
        otpForm: {
          phoneNumber: formatedPhoneNumber,
          emailAddress: email,
        },
      })
      setBiographicAuthInfo({
        phoneNumber: formatedPhoneNumber,
        emailAddress: email,
      })
      if (response?.otpSessionId) {
        setVerificationFormData({
          ...formData,
          otpSessionId: response.otpSessionId,
        })
        setStep(FormSteps.VERIFICATION)
      }
      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
        payload: {
          message: 'Initiate auth successful',
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.INITIATE_AUTH_SUCCESS,
        },
      })
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.ERROR, {
        payload: {
          message: error?.message,
          stack: error?.stack,
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.INITIATE_AUTH_FAILURE,
        },
      })
    }
  }

  async function onVerificationSubmit(formData) {
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
      url: router.route,
    }
    dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
      payload: {
        message: 'OTP verification begin',
      },
      metadata: {
        ...baseMetaData,
        event: DiscreteEvent.OTP_VERIFICATION_BEGIN,
      },
    })
    try {
      await authenticationClient.otpChallenge({
        xCLEARCorrelationInfo,
        otpCode: {
          otpSessionId: verificationFormData.otpSessionId,
          otpCode: formData.verificationCode,
        },
      })
      if (router.query?.desiredPath) {
        router.push(
          {
            pathname: router.query?.desiredPath as string,
            query: {
              otpSessionId: verificationFormData?.otpSessionId,
            },
          },
          router.query?.desiredPath as string
        )
      } else {
        router.push('/')
      }

      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
        payload: {
          message: 'OTP verification successful',
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.OTP_VERIFICATION_SUCCESS,
        },
      })
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      setOtpFailureMessage(
        'Invalid verification code provided, please try again.'
      )
      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.ERROR, {
        payload: {
          message: error?.message,
          stack: error?.stack,
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.OTP_VERIFICATION_FAILURE,
        },
      })
    }
  }

  useEffect(() => {
    document.documentElement.style.backgroundColor = '#FBFBF9'
    return () => {
      document.documentElement.style.backgroundColor = ''
    }
  }, [])

  return (
    <PageContent>
      <Box marginLeft="auto" marginRight="auto" width={['95%', '572px']}>
        <StepTitle textAlign="center">
          TSA PreCheck<sup>®</sup> through CLEAR
        </StepTitle>
        <StepSubTitle textAlign="center" mb={5} fontSize={3}>
          Apply or renew in minutes to enjoy smoother security for five years.
        </StepSubTitle>
      </Box>
      <Box
        width={['95%', '572px']}
        marginLeft="auto"
        marginRight="auto"
        border="1px solid #CCCED1"
        borderRadius="8px"
        paddingX={['20px', '50px']}
        paddingY={['20px', '40px']}
        backgroundColor="neutralsWhite"
      >
        {step === FormSteps.VERIFICATION && (
          <VerificationCodeForm
            formData={verificationFormData}
            onSubmit={onVerificationSubmit}
            otpFailureMessage={otpFailureMessage}
            isLoading={isLoading}
            onSubmitContact={onSubmitContact}
            verificationFormData={verificationFormData}
          />
        )}
        {step === FormSteps.CONTACT && (
          <>
            <img
              className="pb-5 mx-auto"
              src="tsa-pre-logo-auth.svg"
              alt="TSA pre-check with CLEAR logo"
            />

            <StepSubTitle textAlign="center" fontSize={3}>
              Enter your email and mobile number. You'll be sent a one-time code
              to sign in / create an account
            </StepSubTitle>

            <Form onSubmit={handleSubmit(onSubmitContact)}>
              <InputGroup
                data-cy="email"
                placeholder={'Email'}
                {...register('email')}
                error={errors.email?.message}
                mb={2}
                containerProps={{ padding: 0 }}
              />

              <Flex flexDirection="row">
                <InputGroup
                  data-cy={'countryCode'}
                  name="countryCode"
                  disabled
                  value="+1"
                  containerProps={{
                    padding: 0,
                    width: '20%',
                  }}
                />

                <InputGroup
                  data-cy="phoneNumber"
                  placeholder={'Cell Phone Number'}
                  {...register('phoneNumber')}
                  error={errors.phoneNumber?.message}
                  containerProps={{
                    padding: 0,
                    marginLeft: '10px',
                    width: '90%',
                  }}
                />
              </Flex>

              <Checkbox
                text={
                  <Text
                    as="p"
                    color="neutralsTextGray"
                    variant="body"
                    fontSize="12px"
                  >
                    By providing my telephone number and checking this box, I
                    agree to the{' '}
                    <Link
                      target="blank"
                      variant="regular"
                      color="brandDarkBlue"
                      href="/terms"
                    >
                      Terms & Conditions
                    </Link>{' '}
                    and I consent to receive multifactor text messages related
                    to my TSA&nbsp;PreCheck® account.
                  </Text>
                }
                data-cy="acceptTerms"
                {...register('acceptTerms')}
                containerProps={{
                  padding: 0,
                  marginY: 20,
                }}
              />

              <Button
                data-cy="sendLoginCode"
                type="submit"
                text="Send Login Code"
                disabled={
                  Boolean(Object.keys(errors).length) || !isValid || isLoading
                }
                height="3rem !important"
              />
            </Form>
          </>
        )}
      </Box>
    </PageContent>
  )
}

export default AuthPage
